import { Question, AnswerType } from '@/api/api.schemas';
import { RadioGroup } from '@/components/inputs/radio-group';
import { TextInput } from '@/components/inputs/text-input';
import { Slider } from '@/components/inputs/slider';
import { CheckboxGroup } from '@/components/inputs/checkbox-group';
import { TextArea } from '@/components/inputs/text-area';
import { PhotoUpload } from '@/components/uploads/photo-upload/photo-upload';
import { FileUpload } from '@/components/uploads/file/file-upload';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Select } from '@/components/inputs/select';
import styles from './question-renderer.module.css';
import { getResponseName, getResponseOtherName } from '../variable-question-utils';
import { DatePastWithOptionRenderer } from '@/components/inputs/date-with-option';
import { BodyLocation } from '../body-location/body-location';

interface SingleQuestionRendererProps {
  question: Question;
  name: string;
  otherName: string;
}

interface QuestionRendererProps<T extends FieldValues> extends SingleQuestionRendererProps {
  form: UseFormReturn<T>;
  questions: Question[];
}

export const OTHER_CHECKBOX_VALUE = '-1';

function SingleQuestionRenderer({ question, name, otherName }: SingleQuestionRendererProps) {
  switch (question.answerType) {
    case AnswerType.yesNo:
      return (
        <RadioGroup
          name={name}
          variant="toggle"
          color="accent"
          options={[
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes' },
          ]}
          label={question.text}
          subLabel={question.subText}
        />
      );
    case AnswerType.datePast:
      return (
        <TextInput
          name={name}
          label={question.text}
          subLabel={question.subText}
          placeholder={question.placeholderText ?? undefined}
          type="date"
          withIconsIndicators={false}
        />
      );
    case AnswerType.datePastWithOption:
      return (
        <DatePastWithOptionRenderer question={question} name={name} className={styles.oneColumn} />
      );
    case AnswerType.radio:
      return (
        <RadioGroup
          name={name}
          variant="toggle"
          options={question.answers.map((answer) => ({
            value: answer.answerId.toString(),
            label: answer.text,
          }))}
          label={question.text}
          subLabel={question.subText}
        />
      );
    case AnswerType.bodyLocation:
      return <BodyLocation name={name} question={question} />;
    case AnswerType.dropdown:
      return (
        <Select
          className={styles.oneColumn}
          name={name}
          label={question.text}
          subLabel={question.subText}
          placeholder={question.placeholderText ?? undefined}
          options={question.answers.map((answer) => ({
            value: answer.answerId.toString(),
            label: answer.text,
          }))}
        />
      );
    case AnswerType.slider10:
      return <Slider label={question.text} name={name} min={1} max={10} defaultValue={0} />;
    case AnswerType.checkboxOther:
      return (
        <CheckboxGroup
          name={name}
          otherName={otherName}
          otherMaxLength={question.maxLength ?? undefined}
          variant="selector"
          options={question.answers.map((answer) => ({
            value: answer.answerId.toString(),
            label: answer.text,
          }))}
          otherEnabled
          otherValue={OTHER_CHECKBOX_VALUE}
          otherInput="text-area"
          label={question.text}
          subLabel={question.subText}
          otherPlaceholder={question.otherText ?? undefined}
        />
      );

    case AnswerType.textShort:
      return (
        <TextInput
          name={name}
          label={question.text}
          subLabel={question.subText}
          placeholder={question.placeholderText ?? undefined}
        />
      );
    case AnswerType.textLong:
      return (
        <TextArea
          name={name}
          label={question.text}
          subLabel={question.subText}
          rows={3}
          maxLength={question.maxLength ?? undefined}
          placeholder={question.placeholderText ?? undefined}
        />
      );
    case AnswerType.photoUpload:
      return <PhotoUpload name={name} title={question.text} subText={question.subText} />;
    case AnswerType.file:
      return <FileUpload label={question.text} description={question.subText} />;
    default:
      return null;
  }
}

export function QuestionRenderer<T extends FieldValues>({
  questions,
  question,
  name,
  otherName,
  form,
}: QuestionRendererProps<T>) {
  const fieldValue = form.watch(name as Path<T>);

  const answer = question.answers.find((answer) => answer.answerId.toString() === fieldValue);

  const additionalQuestions = answer?.answerId
    ? questions.filter((question) => answer.answerId === question.triggeredByAnswerChoiceId)
    : [];

  return (
    <>
      {!question.triggeredByAnswerChoiceId && (
        <SingleQuestionRenderer question={question} name={name} otherName={otherName} />
      )}
      {additionalQuestions.map((additionalQuestion) => (
        <SingleQuestionRenderer
          key={additionalQuestion.questionId}
          question={additionalQuestion}
          name={getResponseName(additionalQuestion.questionId)}
          otherName={getResponseOtherName(additionalQuestion.questionId)}
        />
      ))}
    </>
  );
}
